// src/components/admin/mail/MailBodyFrame.tsx
import React, { useMemo } from 'react';
import styled from 'styled-components';

const Frame = styled.iframe`
    width: 100%;
    height: 100%;
    min-height: 320px;
    border: none;
    display: block;
    background: #ffffff;
`;

const FrameHolder = styled.div`
    flex: 1;
    min-height: 320px;
    min-width: 0;
    display: flex;
    box-sizing: border-box;
`;

const PlainText = styled.pre`
    margin: 0;
    padding: 1.25rem;
    white-space: pre-wrap;
    word-wrap: break-word;
    font-family: inherit;
    font-size: 0.9rem;
    line-height: 1.6;
    color: #334155;
    box-sizing: border-box;
`;

interface Props {
    html: string | null;
    text: string | null;
    /**
     * Widens the frame's img-src to allow remote hosts. Must be kept in step with the
     * server-side unblocking: the backend restoring the original src is not enough on its
     * own, because this document's own CSP would still refuse to load it.
     */
    allowRemoteImages: boolean;
}

/**
 * Renders an email body inside a locked-down iframe.
 *
 * Two independent reasons this is not `dangerouslySetInnerHTML`, either of which would be
 * sufficient on its own:
 *
 * 1. XSS. The backend sanitizes with jsoup, but sanitizers do get bypassed. Injected inline,
 *    a bypass would execute in the admin origin, where the JWT sits in localStorage and
 *    unlocks the full patient database. Inside this frame it can do nothing at all.
 * 2. CSS containment. Marketing emails ship unscoped `<style>` blocks — `body { font: ... }`,
 *    `* { margin: 0 }` — which injected inline would restyle the whole admin panel until
 *    reload. styled-components protects against class collisions, not element selectors
 *    arriving from an email.
 */
const MailBodyFrame: React.FC<Props> = ({ html, text, allowRemoteImages }) => {
    const srcDoc = useMemo(() => {
        if (!html) return null;
        const imgSrc = allowRemoteImages ? 'data: https:' : 'data:';
        return `<!doctype html>
<html lang="sk"><head>
<meta charset="utf-8">
<meta name="referrer" content="no-referrer">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src ${imgSrc}; style-src 'unsafe-inline'; font-src data:;">
<base target="_blank">
<style>
  html { -webkit-text-size-adjust: 100%; }
  body {
    margin: 0; padding: 20px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px; line-height: 1.6; color: #334155;
    word-wrap: break-word; overflow-wrap: anywhere;
  }
  img, video { max-width: 100% !important; height: auto !important; }
  table { max-width: 100% !important; }
  pre { white-space: pre-wrap; word-wrap: break-word; }
  blockquote {
    margin: 0 0 0 0.8rem; padding-left: 0.8rem;
    border-left: 3px solid #e2e8f0; color: #64748b;
  }
  /* Handles most fixed-width marketing tables; the rest scroll inside this document
     rather than stretching the admin page, because the grid column is minmax(0, 1fr). */
  * { max-width: 100% !important; }
  .blocked-remote-image, .missing-inline-image {
    display: inline-block; min-width: 1px; min-height: 1px;
  }
</style>
</head><body>${html}</body></html>`;
    }, [html, allowRemoteImages]);

    if (!html && text) {
        // React escapes this, so it is safe by construction — no iframe needed.
        return <PlainText>{text}</PlainText>;
    }

    if (!srcDoc) {
        return <PlainText>(Správa neobsahuje žiadny text.)</PlainText>;
    }

    return (
        <FrameHolder>
            <Frame
                title="Obsah správy"
                /*
                 * Neither allow-scripts nor allow-same-origin. Together they are the classic
                 * dangerous pair; here both are simply absent, so the frame has a null opaque
                 * origin and nothing in the email can execute or reach the parent document.
                 *
                 * The cost is that the parent cannot read contentDocument, so the frame cannot
                 * measure its own height — hence it fills the reading pane and scrolls
                 * internally, which is normal two-pane mail-client behaviour anyway.
                 */
                sandbox="allow-popups allow-popups-to-escape-sandbox"
                referrerPolicy="no-referrer"
                srcDoc={srcDoc}
            />
        </FrameHolder>
    );
};

export default MailBodyFrame;
